import { Link } from "@tanstack/react-router";
import { format } from "#/i18n/format.tsx";
import type { Messages } from "#/i18n/index.ts";
import { formatTimestamp } from "#/lib/format.ts";
import { localeParam, type Locale } from "#/lib/locale.ts";
import { NPM_URL, REPOSITORY_URL } from "#/lib/site.ts";

/** What the footer states about the data behind the page. */
export interface FooterData {
  /** pm CLI version the site documents. */
  pmVersion: string;
  /** ISO timestamp of the last data refresh. */
  generatedAt: string;
}

/**
 * Site footer: grouped links, the pm version the site documents and when its
 * data was refreshed (both from data, never typed into copy), and the licence.
 *
 * @param props - Active locale, its copy, and the data line (`null` before the first refresh).
 * @returns The page footer.
 */
export function SiteFooter({ locale, messages, data }: { locale: Locale; messages: Messages; data: FooterData | null }) {
  const params = { locale: localeParam(locale) };
  const t = messages.footer;
  return (
    <footer className="site-footer">
      <div className="page">
        <nav className="site-footer__grid" aria-label={messages.a11y.footerNav}>
          <div>
            <h2>{t.learn}</h2>
            <ul>
              <li>
                <Link to="/{-$locale}/docs/$slug" params={{ ...params, slug: "quickstart" }}>
                  {t.quickstart}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/docs" params={params}>
                  {messages.nav.docs}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/commands" params={params}>
                  {messages.nav.commands}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2>{t.ecosystem}</h2>
            <ul>
              <li>
                <Link to="/{-$locale}/packages" params={params}>
                  {messages.nav.packages}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/hosted" params={params}>
                  {messages.nav.hosted}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/badges" params={params}>
                  {t.badges}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2>{t.project}</h2>
            <ul>
              <li>
                <a href={REPOSITORY_URL}>{messages.nav.github}</a>
              </li>
              <li>
                <a href={`${REPOSITORY_URL}/issues`}>{t.issues}</a>
              </li>
              <li>
                <a href={NPM_URL}>{t.npm}</a>
              </li>
              <li>
                <Link to="/{-$locale}/changelog" params={params}>
                  {messages.nav.changelog}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h2>{t.legal}</h2>
            <ul>
              <li>
                <Link to="/{-$locale}/legal-notice" params={params}>
                  {t.legalNotice}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/privacy-policy" params={params}>
                  {t.privacy}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/terms" params={params}>
                  {t.terms}
                </Link>
              </li>
              <li>
                <Link to="/{-$locale}/cookie-settings" params={params}>
                  {t.storage}
                </Link>
              </li>
            </ul>
          </div>
        </nav>
        <p className="site-footer__meta">
          {data === null
            ? null
            : `${format(t.version, { version: data.pmVersion, when: formatTimestamp(data.generatedAt, locale) })} `}
          {t.license}
        </p>
      </div>
    </footer>
  );
}
